import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CLUBS } from '../shared/clubs'
import { ClubFilter } from './components/ClubFilter'
import { DURATION_LABEL, DurationPicker } from './components/DurationPicker'
import { Header } from './components/Header'
import { NearMe } from './components/NearMe'
import { HoverCard, SlotSheet } from './components/SlotDetails'
import { SourceStatusBar } from './components/SourceStatusBar'
import { WeekGrid, type CellRef } from './components/WeekGrid'
import { WeekNav } from './components/WeekNav'
import { useAvailability } from './hooks/useAvailability'
import type { Place } from '../shared/places'
import { distanceKm, type Point } from './lib/geo'
import { shortDate } from './lib/format'
import { useI18n } from './lib/i18n'
import { useTheme } from './lib/theme'
import { addDays, madeiraToday, startOfWeek, weekDays } from '../shared/time'
import { DURATIONS, optionFor, type Duration } from '../shared/types'

// MapLibre is by far the heaviest dependency and the calendar is the point of
// the page, so the map arrives after it rather than blocking it.
const ClubMap = lazy(() => import('./components/ClubMap').then((m) => ({ default: m.ClubMap })))

// Persist what the visitor turned OFF, not what they left on: storing an
// allow-list would silently hide every club added after their last visit.
const EXCLUDED_KEY = 'padel-madeira:excluded'
const DURATION_KEY = 'padel-madeira:duration'
const ORIGIN_KEY = 'padel-madeira:origin'

/** Forwarded to a real inbox by Cloudflare Email Routing. */
const CONTACT_EMAIL = 'hello@padelmadeira.club'

interface StoredOrigin {
  lat: number
  lon: number
  /** Absent when the browser located you rather than you naming a place. */
  name?: string
  radiusKm: number
}

/** Where you searched from last time. Restored so a reload does not throw away
 *  a location you had to grant permission for, or type. */
function loadOrigin(): StoredOrigin | null {
  try {
    const raw = localStorage.getItem(ORIGIN_KEY)
    if (!raw) return null
    const o = JSON.parse(raw) as StoredOrigin
    const ok =
      Number.isFinite(o?.lat) && Number.isFinite(o?.lon) && Number.isFinite(o?.radiusKm)
    return ok ? o : null
  } catch {
    return null
  }
}

function loadExcluded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXCLUDED_KEY)
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch {
    // Corrupt storage is not worth surfacing; fall through to "everything on".
  }
  return new Set()
}

function loadDuration(): Duration {
  const stored = Number(localStorage.getItem(DURATION_KEY))
  // 60 is the default because every club offers it; 90 is the classic match
  // length but several clubs only sell 60 and 120, so it hides real options.
  return (DURATIONS as readonly number[]).includes(stored) ? (stored as Duration) : 60
}

export default function App() {
  const { theme, toggle } = useTheme()
  const { t, locale } = useI18n()
  const today = useMemo(() => madeiraToday(), [])

  const [offset, setOffset] = useState(0)
  const weekStart = useMemo(() => addDays(startOfWeek(today), offset * 7), [today, offset])
  const days = useMemo(() => weekDays(weekStart), [weekStart])
  const weekEnd = days[6]

  const [excluded, setExcluded] = useState<Set<string>>(loadExcluded)
  useEffect(() => {
    localStorage.setItem(EXCLUDED_KEY, JSON.stringify([...excluded]))
  }, [excluded])

  const selected = useMemo(
    () => new Set(CLUBS.filter((c) => !excluded.has(c.id)).map((c) => c.id)),
    [excluded],
  )

  const [duration, setDuration] = useState<Duration>(loadDuration)
  useEffect(() => {
    localStorage.setItem(DURATION_KEY, String(duration))
  }, [duration])

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const { data, loading, error } = useAvailability(weekStart, weekEnd)

  const clubsById = useMemo(() => new Map(CLUBS.map((c) => [c.id, c])), [])
  const allSlots = data?.slots ?? []

  // A start only counts if the length you asked for actually fits in it.
  const longEnough = useMemo(
    () => allSlots.filter((s) => optionFor(s, duration) !== undefined),
    [allSlots, duration],
  )

  const slots = useMemo(
    () => longEnough.filter((s) => selected.has(s.clubId)),
    [longEnough, selected],
  )

  // Counts come from the club-unfiltered set, so a chip advertises what turning
  // it back on would actually add at the current duration.
  const counts = useMemo(() => {
    const map = new Map<string, number>()
    for (const s of longEnough) map.set(s.clubId, (map.get(s.clubId) ?? 0) + 1)
    return map
  }, [longEnough])

  // The furthest any selected club has published. Days past it are unknown to
  // every club on screen, so the grid marks them rather than showing an empty
  // column that reads as "fully booked".
  const coveredThrough = useMemo(() => {
    const covered = (data?.coverage ?? []).filter((c) => selected.has(c.clubId))
    if (covered.length === 0) return undefined
    return covered.reduce((max, c) => (c.through > max ? c.through : max), covered[0].through)
  }, [data, selected])

  const [hover, setHover] = useState<CellRef | null>(null)
  const [sheet, setSheet] = useState<CellRef | null>(null)
  const canHover = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches,
    [],
  )

  const setSelection = useCallback((ids: string[]) => {
    const keep = new Set(ids)
    setExcluded(new Set(CLUBS.filter((c) => !keep.has(c.id)).map((c) => c.id)))
  }, [])

  const toggleClub = useCallback((id: string) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // ---- near me ----
  const stored = useMemo(loadOrigin, [])
  const [origin, setOrigin] = useState<Point | null>(
    stored ? { lat: stored.lat, lon: stored.lon } : null,
  )
  const [originName, setOriginName] = useState<string | undefined>(stored?.name)
  const [radiusKm, setRadiusKm] = useState(stored?.radiusKm ?? 15)

  useEffect(() => {
    if (!origin) {
      localStorage.removeItem(ORIGIN_KEY)
      return
    }
    const next: StoredOrigin = { lat: origin.lat, lon: origin.lon, name: originName, radiusKm }
    localStorage.setItem(ORIGIN_KEY, JSON.stringify(next))
  }, [origin, originName, radiusKm])

  const withinRadius = useMemo(() => {
    if (!origin) return null
    return CLUBS.filter((c) => distanceKm(origin, { lat: c.lat, lon: c.lon }) <= radiusKm)
  }, [origin, radiusKm])

  /**
   * The radius seeds the selection, it does not own it. Once it has been
   * applied you are free to toggle individual clubs, and those edits survive a
   * reload - so this only fires when the location or the radius actually
   * changes. Seeding it with the restored value is what stops a reload from
   * resetting the very selection we just restored.
   */
  const appliedRadius = useRef<string | null>(
    stored ? `${stored.lat},${stored.lon},${stored.radiusKm}` : null,
  )

  useEffect(() => {
    if (!origin) return
    const key = `${origin.lat},${origin.lon},${radiusKm}`
    if (appliedRadius.current === key) return
    appliedRadius.current = key
    setSelection(
      CLUBS.filter((c) => distanceKm(origin, { lat: c.lat, lon: c.lon }) <= radiusKm).map(
        (c) => c.id,
      ),
    )
  }, [origin, radiusKm, setSelection])

  const clearOrigin = useCallback(() => {
    setOrigin(null)
    setOriginName(undefined)
    appliedRadius.current = null
    setSelection(CLUBS.map((c) => c.id))
  }, [setSelection])

  const pickPlace = useCallback((place: Place) => {
    setOrigin({ lat: place.lat, lon: place.lon })
    setOriginName(place.name)
  }, [])

  const locateMe = useCallback((point: Point) => {
    setOrigin(point)
    setOriginName(undefined)
  }, [])

  // Hover is a preview and only exists where a pointer does. A click always
  // opens the full panel - on touch it is the only route in, and on desktop it
  // is how you reach the booking links and anything the preview capped off.
  const onCell = useCallback((cell: CellRef | null) => canHover && setHover(cell), [canHover])
  const onPick = useCallback((cell: CellRef) => {
    setHover(null)
    setSheet(cell)
  }, [])

  const emptyWeek = !loading && slots.length === 0

  return (
    <div
      className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6"
      style={{ '--gutter': '3.25rem', '--row-h': '30px' } as React.CSSProperties}
    >
      <Header theme={theme} onToggleTheme={toggle} />

      {/* "Where" is one idea, so the map and every control that narrows it live
          in a single band - side by side once there is room, stacked below. */}
      <section className="mb-3 grid gap-3 lg:grid-cols-12">
        <div className="card overflow-hidden max-lg:h-[180px] lg:col-span-7 lg:h-[380px]">
          <Suspense
            fallback={<div className="h-full w-full" style={{ background: 'var(--surface-2)' }} />}
          >
            <ClubMap
              clubs={CLUBS}
              selected={selected}
              onToggle={toggleClub}
              theme={theme}
              origin={origin}
              radiusKm={radiusKm}
            />
          </Suspense>
        </div>

        <div className="card flex min-w-0 flex-col gap-3 p-3 lg:col-span-5 lg:h-[380px] lg:overflow-y-auto">
          <NearMe
            origin={origin}
            originName={originName}
            radiusKm={radiusKm}
            matches={withinRadius?.length ?? 0}
            onLocate={locateMe}
            onPlace={pickPlace}
            onRadius={setRadiusKm}
            onClear={clearOrigin}
          />

          <div className="h-px shrink-0" style={{ background: 'var(--border)' }} />

          <ClubFilter
            clubs={CLUBS}
            selected={selected}
            onToggle={toggleClub}
            onSet={setSelection}
            counts={counts}
          />
        </div>
      </section>

      {/* The calendar and the controls that drive it are one object, so the
          toolbar is the card's header rather than a row floating above it. */}
      <section className="card overflow-hidden">
        <div className="border-b px-3 py-2.5" style={{ borderColor: 'var(--border)' }}>
          <WeekNav
            weekStart={weekStart}
            weekEnd={weekEnd}
            offset={offset}
            loading={loading}
            updatedAt={data?.fetchedAt}
            onOffset={setOffset}
          >
            <div className="flex items-center gap-2 max-sm:pt-0.5">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {t.playFor}
              </span>
              <DurationPicker value={duration} label={t.playFor} onChange={setDuration} />
            </div>
          </WeekNav>
        </div>

        <WeekGrid
          days={days}
          slots={slots}
          clubs={clubsById}
          today={today}
          now={now}
          coveredThrough={coveredThrough}
          active={hover ?? sheet}
          onCell={onCell}
          onPick={onPick}
        />
      </section>

      {coveredThrough !== undefined && coveredThrough < weekEnd && (
        <p className="mt-3 text-center text-[11px]" style={{ color: 'var(--text-faint)' }}>
          {t.notOpenYetNote(shortDate(coveredThrough, locale))}
        </p>
      )}

      {emptyWeek && (
        <p className="mt-3 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
          {selected.size === 0 ? t.noClubs : t.nothingFree(DURATION_LABEL[duration])}
        </p>
      )}

      {error && (
        <p className="mt-3 text-center text-sm" style={{ color: '#f87171' }}>
          {t.loadError}
        </p>
      )}

      <SourceStatusBar statuses={data?.sources ?? []} />

      <footer
        className="mt-6 mb-2 text-[11px] leading-relaxed"
        style={{ color: 'var(--text-faint)' }}
      >
        <p className="mb-1.5">
          {t.contactPrompt}{' '}
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Padel Madeira')}`}
            className="underline underline-offset-2"
            style={{ color: 'var(--text-muted)' }}
          >
            {CONTACT_EMAIL}
          </a>
        </p>
        {t.footer}{' '}
        <span style={{ color: 'var(--text-faint)' }}>
          Map{' '}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noreferrer noopener"
            className="underline underline-offset-2"
          >
            © OpenStreetMap
          </a>{' '}
          contributors,{' '}
          <a
            href="https://carto.com/attributions"
            target="_blank"
            rel="noreferrer noopener"
            className="underline underline-offset-2"
          >
            © CARTO
          </a>
          .
        </span>
      </footer>

      {canHover && hover && !sheet && (
        <HoverCard cell={hover} clubs={clubsById} duration={duration} />
      )}
      {sheet && (
        <SlotSheet
          cell={sheet}
          clubs={clubsById}
          duration={duration}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  )
}
